import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  esCamion,
  formatMoney,
  formatQuantity,
  hoy,
  sumarDias,
} from '@abastece/shared';
import type { Pedido } from '@abastece/core';
import { db, getMeta } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { diaDelTurno, primeroDelDia, textoDelDia } from '../lib/dia.js';
import { useHoy } from '../lib/hoy.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { totalConEnvio } from '../lib/pedidos.js';
import { enlaceWhatsApp } from '../lib/recibo.js';
import { usePuede } from '../lib/sesion.js';
import { tablero } from '../lib/vencimientos.js';
import { contarAcabandose } from '../routes/Reponer.js';
import { pagosEnCola } from '../routes/Transferencias.js';
import { sincronizar, suscribirSync, type SyncState } from '../sync/engine.js';
import { idiomaActual, localeActual, useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Chip, Ico, type NombreIcono, type Tinte } from './Iconos.js';

/**
 * Lo del día: what the day holds, for the steps of «Empezar el día» and «Terminar el día» that say
 * it. The same figures the home screen's notices count, with the same people allowed to see each
 * one, and a way to each.
 */

/**
 * Whether starting the till on this device now would be the day's first (`primeroDelDia`): what
 * the buttons that lead there call it, «Empezar el día» or «Empezar mi turno». Undefined while it
 * is read, for a screen to wait rather than say one and then the other.
 */
export function useEmpiezaElDia(ubicacion = 'tienda'): boolean | undefined {
  const dia = useHoy();
  return useLiveQuery(
    async () => {
      const dispositivoId = await getMeta('dispositivoId');
      if (!dispositivoId) return true;
      const cerradas = await db.sesiones.where('estado').equals('cerrada').toArray();
      return primeroDelDia(cerradas, dispositivoId, ubicacion, dia);
    },
    [ubicacion, dia],
  );
}

/**
 * The orders still to deliver: today's, those that should have gone already included, or
 * tomorrow's alone. Undefined while they are read.
 */
export function usePedidosPendientes(cuando: 'hoy' | 'manana'): Pedido[] | undefined {
  const dia = useHoy();
  return useLiveQuery(async () => {
    const manana = sumarDias(dia, 1);
    return (await db.pedidos.where('estado').equals('pendiente').toArray())
      .filter((p) => (cuando === 'hoy' ? p.fechaEntrega <= dia : p.fechaEntrega === manana))
      .sort((a, b) => a.fechaEntrega.localeCompare(b.fechaEntrega));
  }, [cuando, dia]);
}

/**
 * Each order: whose it is, what goes in it, and an order that should have gone already says so.
 * Nothing until the clients and products are read: drawn before, every order said «Cliente
 * desconocido» for a moment.
 */
export function ListaDePedidos({ pedidos }: { pedidos: readonly Pedido[] }) {
  const t = useT();
  const nombre = useNombreProducto();
  const clientes = useLiveQuery(() => db.clientes.toArray(), []);
  const productos = useLiveQuery(() => db.productos.toArray(), []);
  const dia = useHoy();
  if (!clientes || !productos) return null;
  return (
    <ul className="flex flex-col gap-3">
      {pedidos.map((p) => {
        const cliente = clientes.find((c) => c.id === p.clienteId)?.nombre ?? t('pedidos.clienteDesconocido');
        return (
          <li key={p.id} className="tarjeta flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              {/* Whole, on two lines if it takes them: «Pulpería Doñ…» does not say who to take it to. */}
              <p className="min-w-0 font-semibold wrap-break-word">{cliente}</p>
              <p className="tabular shrink-0 font-semibold">{formatMoney(totalConEnvio(p))}</p>
            </div>
            {p.fechaEntrega < dia && (
              <p className="text-xs font-medium text-amber-300">
                {t('turno.pedidoAtrasado', {
                  fecha: new Date(`${p.fechaEntrega}T12:00:00Z`).toLocaleDateString(localeActual(), {
                    day: 'numeric',
                    month: 'short',
                    timeZone: 'UTC',
                  }),
                })}
              </p>
            )}
            <ul className="flex flex-col gap-0.5 text-sm text-slate-400">
              {p.lineas.map((l, i) => {
                const producto = productos.find((x) => x.id === l.productoId);
                return (
                  <li key={i} className="flex gap-2">
                    <span className="tabular shrink-0">{formatQuantity(l.cantidad)} ×</span>
                    <span className="min-w-0 truncate">{producto ? nombre(producto) : '—'}</span>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

/** A notice that needs somebody, as on the home screen, and where it is seen to. */
export interface Aviso {
  clave: string;
  texto: string;
  icono: NombreIcono;
  tinte: Tinte;
  a: '/reponer' | '/vencimientos' | '/inventario' | '/transferencias' | '/efectivo';
  negativos?: true;
}

/**
 * The stock that wants seeing to before the day's selling: products running out, lots expiring,
 * products sold past what the books held. Each only for whoever may act on it, as on the home
 * screen. Undefined while it is worked out.
 */
export function useAvisosDeInventario(): Aviso[] | undefined {
  const t = useT();
  const puedeComprar = usePuede('recibir_compras');
  const puedeAjustar = usePuede('ajustar_inventario');
  const puedeConfigurar = usePuede('editar_productos');
  const datos = useLiveQuery(
    async () => {
      const [acabandose, negativos, porVencer] = await Promise.all([
        puedeComprar ? contarAcabandose() : 0,
        puedeAjustar
          ? db.existencias.filter((e) => e.cantidad < 0 && !esCamion(e.ubicacion)).count()
          : 0,
        puedeConfigurar
          ? (async () => tablero(await db.lotes.toArray(), (await readSettings()).vencimientoAlertaDias).length)()
          : 0,
      ]);
      return { acabandose, negativos, porVencer };
    },
    [puedeComprar, puedeAjustar, puedeConfigurar],
  );
  if (!datos) return undefined;
  const avisos: Aviso[] = [];
  if (datos.acabandose > 0)
    avisos.push({ clave: 'acabandose', texto: t('inicio.seEstanAcabando', { n: datos.acabandose }), icono: 'reponer', tinte: 'ambar', a: '/reponer' });
  if (datos.porVencer > 0)
    avisos.push({ clave: 'vencer', texto: t('inicio.lotesPorVencer', { n: datos.porVencer }), icono: 'vencimientos', tinte: 'rosa', a: '/vencimientos' });
  if (datos.negativos > 0)
    avisos.push({ clave: 'negativos', texto: t('inicio.negativos', { n: datos.negativos }), icono: 'inventario', tinte: 'ambar', a: '/inventario', negativos: true });
  return avisos;
}

/**
 * What the owner has left at the end of the day: transfers nobody has confirmed, and cash the
 * tills handed over that nobody has received. Each only for whoever may see to it. Undefined while
 * it is worked out. (Trucks not back and settled were the third, until «Reparto» went, 2026-10-04:
 * a route left open from before is said on the home screen, and settled on its own phone.)
 */
export function usePendientesDelDueno(): Aviso[] | undefined {
  const t = useT();
  const puedeConfirmar = usePuede('confirmar_transferencia');
  const puedeRecibir = usePuede('recibir_efectivo');
  const datos = useLiveQuery(
    async () => {
      const [cola, sesiones] = await Promise.all([
        puedeConfirmar ? pagosEnCola() : [],
        puedeRecibir ? db.sesiones.toArray() : [],
      ]);
      // How many tills, not how much: the owner counts it blind (`inicio.porRecibir`).
      const porRecibir = sesiones.filter((s) => (s.porRecibir ?? 0) > 0 || (s.porRecibirDolares ?? 0) > 0).length;
      return { transferencias: cola.length, porRecibir };
    },
    [puedeConfirmar, puedeRecibir],
  );
  if (!datos) return undefined;
  const avisos: Aviso[] = [];
  if (datos.transferencias > 0)
    avisos.push({
      clave: 'transferencias',
      texto: t('inicio.transferenciasPendientes', { n: datos.transferencias }),
      icono: 'transferencias',
      tinte: 'ambar',
      a: '/transferencias',
    });
  if (datos.porRecibir > 0)
    avisos.push({
      clave: 'porRecibir',
      texto: t('inicio.porRecibir', { n: datos.porRecibir }),
      icono: 'recibir',
      tinte: 'ambar',
      a: '/efectivo',
    });
  return avisos;
}

/** The notices, each a way to where it is seen to. */
export function ListaDeAvisos({ avisos }: { avisos: readonly Aviso[] }) {
  return (
    <div className="flex flex-col gap-3">
      {avisos.map((a) => (
        <Link
          key={a.clave}
          to={a.a}
          {...(a.negativos ? { search: { negativos: true } } : {})}
          className="tarjeta-accion flex items-center gap-3 p-3.5"
        >
          <Chip icono={a.icono} tinte={a.tinte} />
          <span className="min-w-0 flex-1 text-sm font-medium">{a.texto}</span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-500" />
        </Link>
      ))}
    </div>
  );
}

/**
 * The day's summary at this drawer (`textoDelDia`): every shift closed here today, the one just
 * ended among them. Printed on the till's roll, or sent to the owner by WhatsApp, to whoever is
 * picked: the owner is not always at the shop when it closes, and this is how they hear of the day.
 */
export function ResumenDelDia({ dispositivoId, ubicacion }: { dispositivoId: string; ubicacion: string }) {
  const t = useT();
  /**
   * The shop's own name heads it, so nothing is said until it is read: drawn with the default
   * first, the summary on screen was not the one sent or printed a moment later.
   */
  const config = useLiveQuery(() => readSettings(), []);
  const texto = useLiveQuery(
    async () => {
      if (!config) return undefined;
      const [sesiones, usuarios] = await Promise.all([
        db.sesiones.where('estado').equals('cerrada').toArray(),
        db.usuarios.toArray(),
      ]);
      const nombre = (id: string | null | undefined) => usuarios.find((u) => u.id === id)?.nombre ?? '';
      const aqui = sesiones.filter((s) => s.dispositivoId === dispositivoId && s.ubicacion === ubicacion && s.cerradaEn !== null);
      // The day of the shift that just ended (`diaDelTurno`): after midnight, still the night before's.
      const ultima = aqui.reduce<(typeof aqui)[number] | undefined>((a, s) => (!a || s.cerradaEn! > a.cerradaEn! ? s : a), undefined);
      const dia = ultima ? diaDelTurno(ultima) : hoy();
      const turnos = aqui
        .filter((s) => diaDelTurno(s) === dia)
        .sort((a, b) => a.abiertaEn.localeCompare(b.abiertaEn))
        .map((sesion) => ({ sesion, abrio: nombre(sesion.usuarioId), cerro: sesion.cerradaPor ? nombre(sesion.cerradaPor) : null }));
      return textoDelDia(turnos, config.negocioNombre, dia, t, localeActual());
    },
    // `t` is the same function in every language: the language is what changes the text.
    [dispositivoId, ubicacion, config?.negocioNombre, config === undefined, idiomaActual()],
  );
  if (texto === undefined) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <a
          className="btn-secundario min-w-0 flex-1 px-3 text-sm"
          href={enlaceWhatsApp(texto)}
          target="_blank"
          rel="noreferrer"
        >
          <Ico icono="whatsapp" />
          {t('turno.enviarWhatsapp')}
        </a>
        <button type="button" className="btn-secundario min-w-0 flex-1 px-3 text-sm" onClick={() => imprimirTexto(texto)}>
          <Ico icono="imprimir" />
          {t('comun.imprimir')}
        </button>
      </div>
      <pre className="tabular overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs leading-relaxed whitespace-pre">{texto}</pre>
    </div>
  );
}

/**
 * Whether the day has reached the server, said before the phone goes off: the shop works with no
 * signal, and what has not gone up is only on this phone. It asks for a sync on arriving, and says
 * what is still to go, that there is no signal, or that it is all there.
 */
export function GuardadoEnServidor() {
  const t = useT();
  const [estado, setEstado] = useState<SyncState | null>(null);
  useEffect(() => suscribirSync(setEstado), []);
  useEffect(() => {
    void sincronizar();
  }, []);
  if (!estado) return null;

  const { fase, pendientes } = estado;
  let tinte: Tinte = 'ambar';
  let icono: NombreIcono = 'aviso';
  let texto: string;
  if (fase === 'practica') {
    tinte = 'neutro';
    icono = 'aprender';
    texto = t('turno.guardadoPractica');
  } else if (fase === 'sin-conexion') {
    texto = t('turno.guardadoSinConexion', { n: pendientes });
  } else if (fase === 'error') {
    texto = t('turno.guardadoError', { error: estado.ultimoError ?? '' });
  } else if (fase === 'sin-registrar' || fase === 'dado-de-baja') {
    texto = t('turno.guardadoNoSube');
  } else if (fase === 'sincronizando' || pendientes > 0) {
    tinte = 'cielo';
    icono = 'sincronizar';
    texto = t('turno.guardando', { n: pendientes });
  } else {
    tinte = 'esmeralda';
    icono = 'listo';
    texto = t('turno.guardadoTodo');
  }
  const reintentar = fase === 'sin-conexion' || fase === 'error';
  return (
    <div className="flex flex-col gap-3">
      <div className="tarjeta flex items-start gap-3" role="status">
        <Chip icono={icono} tinte={tinte} />
        <p className="min-w-0 flex-1 self-center text-sm">{texto}</p>
      </div>
      {reintentar && (
        <button type="button" className="btn-secundario" onClick={() => void sincronizar()}>
          <Ico icono="sincronizar" />
          {t('turno.reintentar')}
        </button>
      )}
    </div>
  );
}
